import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService, api } from '../services/api';
import { getAuthToken, setAuthToken, removeAuthToken, isValidTokenFormat } from '../services/auth-storage';
import { User as ApiUser } from '../types/user';
import config from '../config.json';

// Types
export type UserRole = 'admin' | 'student';

export interface User {
  id: string;
  fullName: string;
  email: string;
  role: UserRole;
  isAdmin?: boolean; // Added to fix type errors
  avatarUrl?: string; // Added to fix type errors
  organization?: string;
}

interface Session {
  token: string;
  user: User;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (name: string, email: string, password: string, invitationKey: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  validateInvitationKey: (key: string) => Promise<{ 
    valid: boolean; 
    message: string; 
    data: any;
    organizationId?: string;
    organizationName?: string;
    expiresAt?: string; 
  }>;
  registerWithInvitation: (
    email: string,
    password: string,
    fullName: string,
    invitationKeyId: string,
    organizationId: string
  ) => Promise<{ success: boolean; user: any }>;
  completeRegistration: (
    email: string,
    password: string,
    fullName: string,
    invitationKey: string
  ) => Promise<{ success: boolean; user: any; error?: string }>;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Function to set up user session from token
  const setupUserSession = async (token: string) => {
    //console.log('Setting up user session from token');
    
    try {
      // Only verify token format if validation is working correctly
      if (!isValidTokenFormat(token)) {
        console.warn('Token validation failed during session setup');
        // We'll still try to use the token as the server will validate it properly
      }
      
      // Add the token to the authorization header for all subsequent requests
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      //console.log('Set Authorization header for API calls');
      
      const userData = await authService.getCurrentUser();
      //console.log('User data from getCurrentUser:', userData);
      
      if (userData) {
        // Create a properly typed user object
        const user: User = {
          id: userData.id,
          fullName: userData.fullName || 'User',
          email: userData.email,
          role: userData.isAdmin ? 'admin' : 'student',
          isAdmin: userData.isAdmin,
          avatarUrl: userData.avatarUrl,
          organization: ''
        };
        
        //console.log('Processed user data:', user);
        
        setUser(user);
        setSession({ token, user });
        return true;
      } else {
        console.error('getCurrentUser returned no data despite valid token');
        return false;
      }
    } catch (error) {
      console.error('Error during setupUserSession:', error);
      return false;
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        setIsLoading(true);
        const token = getAuthToken();
        
        //console.log('Session check - Token in storage:', token ? 'exists' : 'missing');
        
        if (token) {
          // Instead of removing token immediately on validation failure,
          // try to use it first and let the server decide if it's valid
          const sessionSetupSuccess = await setupUserSession(token);
          
          if (!sessionSetupSuccess) {
            console.warn('Session setup failed - token may be invalid');
            removeAuthToken();
            delete api.defaults.headers.common['Authorization'];
          }
        } else {
          //console.log('No token found in session storage');
        }
      } catch (error) {
        console.error('Session check error:', error);
        removeAuthToken();
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login(email, password);
      
      const apiUser = response.user;
      //console.log('Login response user:', apiUser);
      //console.log('Login response token:', response.token ? 'received' : 'missing');
      
      if (!response.token) {
        console.error('No token received from login response');
        return false;
      }
      
      // Explicitly set the token in authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${response.token}`;
      
      const user: User = {
        id: apiUser.id,
        fullName: apiUser.fullName || 'User',
        email: apiUser.email,
        role: apiUser.isAdmin ? 'admin' : 'student',
        isAdmin: apiUser.isAdmin,
        avatarUrl: apiUser.avatarUrl,
        organization: ''
      };
      
      setUser(user);
      setSession({ token: response.token, user });
      
      // Verify token was set properly
      const storedToken = getAuthToken();
      //console.log('After login - Token in storage:', storedToken ? 'exists' : 'missing');
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const signup = async (name: string, email: string, password: string, invitationKey: string) => {
    try {
      await authService.register(email, password, name, invitationKey);
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  };

  const logout = () => {
    // Clear auth state
    setUser(null);
    setSession(null);
    
    // First explicitly remove auth tokens from all storage
    removeAuthToken();
    
    // Then clear all session storage 
    sessionStorage.clear();
    
    // Also clear localStorage to ensure no auth data persists
    localStorage.removeItem(config.auth.tokenKey);
    
    // Clear the Authorization header
    delete api.defaults.headers.common['Authorization'];
    
    // Use a timeout to ensure storage is cleared before redirect
    setTimeout(() => {
      window.location.href = '/';
    }, 50);
  };

  const validateInvitationKey = async (key: string) => {
    try {
      const response = await authService.validateInvitationKey(key);
      return {
        valid: true,
        message: 'Valid invitation key',
        organizationId: response.organizationId,
        organizationName: response.organizationName,
        expiresAt: response.expiresAt,
        data: response,
      };
    } catch (error) {
      console.error('Error validating invitation key:', error);
      return {
        valid: false,
        message: 'Invalid invitation key',
        data: null,
      };
    }
  };

  const registerWithInvitation = async (
    email: string,
    password: string,
    fullName: string,
    invitationKeyId: string,
    organizationId: string
  ) => {
    try {
      await authService.register(email, password, fullName, invitationKeyId);
      return { success: true, user: null };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, user: null };
    }
  };

  const completeRegistration = async (
    email: string,
    password: string,
    fullName: string,
    invitationKey: string
  ) => {
    try {
      const result = await authService.completeRegistration(email, password, fullName, invitationKey);
      
      if (result.success && result.token && result.user) {
        // Set the user in our state
        const user: User = {
          id: result.user.id,
          fullName: result.user.fullName || 'User',
          email: result.user.email,
          role: result.user.isAdmin ? 'admin' : 'student',
          isAdmin: result.user.isAdmin,
          organization: result.user.organization?.name || ''
        };
        
        setUser(user);
        setSession({ token: result.token, user });
        
        return { success: true, user };
      }
      
      return { success: false, user: null, error: 'Registration completed but login failed' };
    } catch (error: any) {
      console.error('Complete registration error:', error);
      return { 
        success: false, 
        user: null, 
        error: error.message || 'Registration failed'
      };
    }
  };

  const value = {
    user,
    session,
    isLoading,
    login,
    signup,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    validateInvitationKey,
    registerWithInvitation,
    completeRegistration,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
